import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Select, { components } from "react-select"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import Spinner from "react-bootstrap/Spinner"
import { useAppDispatch, useAppSelector } from "../../redux/store"
import {
  IconBalance,
  IconCheck,
  IconConfirm,
} from "@/components/utils/Icons/CustomIcons"
import { checkDiscount } from "@/redux/slices/events/eventsSlice"
import api from "@/api/api"
import "./CreditsPage.scss"

const TAP_PUBLISHABLE_KEY = import.meta.env.VITE_REACT_APP_TAP_PUBLISHABLE_KEY

type Pack = any | ""

const CreditsPage = () => {
  const { discount } = useAppSelector((state) => state.events)
  const { balance, lang, first_name, last_name, email, phone } = useAppSelector(
    (state) => state.auth,
  )
  const { i18n, t } = useTranslation()
  const [promo, setPromo] = useState("")
  const [pack, setPack] = useState<Pack>("")
  const [error, setError] = useState(false)
  const [purchaseItems, setPurchaseItems] = useState<any[]>([])
  const [isPurchasing, setIsPurchasing] = useState(false)

  const dispatch = useAppDispatch()

  function initGoSell() {
    goSell.config({
      gateway: {
        publicKey: TAP_PUBLISHABLE_KEY,
        merchantId: null,
        language: lang,
        contactInfo: true,
        supportedCurrencies: "all",
        supportedPaymentMethods: "all",
        saveCardOption: false,
        customerCards: true,
        notifications: "standard",
        callback: (response: any) => {
          console.log("response", response)
        },
        onClose: () => {
          goSell.closeModal()
        },
        labels: {
          cardNumber: "Card Number",
          expirationDate: "MM/YY",
          cvv: "CVV",
          cardHolder: "Name on Card",
          actionButton: "Pay",
        },
        style: {
          base: {
            color: "#535353",
            lineHeight: "18px",
            fontFamily: "sans-serif",
            fontSmoothing: "antialiased",
            fontSize: "16px",
            "::placeholder": {
              color: "rgba(0, 0, 0, 0.26)",
              fontSize: "15px",
            },
          },
          invalid: {
            color: "red",
            iconColor: "#fa755a",
          },
        },
      },
      customer: {
        first_name: first_name,
        middle_name: null,
        last_name: last_name,
        email: email,
        phone: {
          country_code: phone?.substring(0, 3) || "965",
          number: phone?.substring(3) || "99999999",
        },
      },
      order: {
        discount: 0,
        amount: pack !== "" ? pack.amount : 0,
        currency: "SAR",
        shipping: null,
        taxes: null,
      },
      transaction: {
        mode: "charge",
        charge: {
          saveCard: false,
          threeDSecure: true,
          description: "Mazoom credits",
          statement_descriptor: "statement_descriptor",
          metadata: {
            event_id: null,
            type: "credits",
            discount_code_id: discount.discount_id,
          },
          receipt: {
            email: true,
            sms: false,
          },
          redirect: `${window.location.origin}/credits/result`,
          post: `${import.meta.env.VITE_REACT_APP_API_BASE_URL
            }/ecards/payment-webhook`,
          hashstring: "",
        },
      },
    })
  }

  useEffect(() => {
    api.events
      .getPurchaseItems()
      .then((res: any) => {
        if (Array.isArray(res?.data)) {
          setPurchaseItems(res.data)
        } else if (Array.isArray(res?.data?.data)) {
          setPurchaseItems(res.data.data)
        }
      })
      .catch(() => setPurchaseItems([]))
  }, [])

  useEffect(() => {
    // no-op: label comes from purchase items
    console.log(pack)
  }, [pack])

  // Debug useEffect for package pre-selection
  const location = useLocation();

  useEffect(() => {
    console.log("CreditsPage pre-select check:", {
      locationState: location.state,
      items: purchaseItems.length,
      fromPricing: location.state?.fromPricing,
    });

    if (location.state?.fromPricing && purchaseItems.length > 0) {
      const selectedPackage = location.state?.selectedPackage;
      const guestCount = location.state?.guestCount;

      console.log("Attempting to pre-select package:", { selectedPackage, guestCount });

      if (selectedPackage && guestCount) {
        let bestMatch: any = null;
        let minDifference = Infinity;

        purchaseItems.forEach((item: any) => {
          const difference = Math.abs(item.guest_amount - guestCount);
          if (difference < minDifference) {
            minDifference = difference;
            bestMatch = item;
          }
        });

        if (bestMatch) {
          console.log("Found best match:", bestMatch);
          setPack(bestMatch);
          console.log("Set pack to:", bestMatch);
        }
      }
    }
  }, [purchaseItems, location.state]);

  return (
    <>
      <div className="credits">
        <div className="container container-credits">
          <h2 className="caption-two credits-title">Credits</h2>
          <div className="credits-main">
            {/* Show package info when coming from pricing, otherwise show balance */}
            {location.state?.fromPricing && location.state?.selectedPackage ? (
              <div className="credits-main__balance credits-main__section">
                <h4 className="credits-main__title">{t("credits.package")} </h4>
                <div className="credits-main__balance-description">
                  <span className="credits-main__balance-text">
                    {location.state.selectedPackage.name[i18n.language === 'ar' ? 'ar' : 'en'] || location.state.selectedPackage.name}
                  </span>
                  <IconBalance />
                </div>
              </div>
            ) : (
              <div className="credits-main__balance credits-main__section">
                <h4 className="credits-main__title">{t("credits.yourBalance")} </h4>
                <div className="credits-main__balance-description">
                  <span className="credits-main__balance-text">
                    {balance} credits
                  </span>
                  <IconBalance />
                </div>
              </div>
            )}
            <div className="credits-main__buy credits-main__section">
              <h4 className="credits-main__title">Buy credits:</h4>
              <Select
                // menuIsOpen={true}
                placeholder="Select a credit pack"
                options={purchaseItems}
                isSearchable={false}
                value={pack === "" ? null : pack}
                onChange={(val: any) => {
                  setPack(val)
                  console.log(val)
                }}
                getOptionValue={(opt: any) => String(opt.id)}
                getOptionLabel={(opt: any) => `${opt.guest_amount} credits • ${opt.amount} SAR`}
                components={{
                  MenuList: (props) => (
                    <div>
                      <div style={{
                        padding: "8px 10px",
                        fontSize: "12px",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}>
                        Choose a credit pack
                      </div>
                      <components.MenuList {...props} />
                    </div>
                  ),
                  Option: (props: any) => {
                    const { data, isSelected, innerProps } = props
                    return (
                      <div
                        {...innerProps}
                        style={{
                          padding: "10px 12px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          background: isSelected ? "#F2F4F7" : "#FFF",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontSize: 14, color: "#0B0C0E" }}>
                          {data.guest_amount} credits
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                          {data.amount} SAR
                        </div>
                      </div>
                    )
                  },
                  SingleValue: (props: any) => {
                    const { data } = props
                    return (
                      <components.SingleValue {...props}>
                        <span style={{ fontWeight: 600 }}>{data.amount} SAR</span>
                        <span style={{ marginLeft: 8, color: "#6b7280", fontSize: 12 }}>
                          • {data.guest_amount} credits
                        </span>
                      </components.SingleValue>
                    )
                  },
                }}
                styles={{
                  container: (state: any) => ({
                    ...state,
                    outline: "none",
                    border: "0",
                    position: "relative",
                    boxShadow: "none",
                    "@media (max-width: 576px)": {
                      width: "100%",
                    },
                  }),
                  valueContainer: (state: any) => ({
                    ...state,
                    display: "flex",
                    justifyContent: "end",
                    padding: "0 10px 0 0",
                    "@media (max-width: 576px)": {
                      justifyContent: "start",
                      height: "34px",
                    },
                  }),
                  placeholder: (state: any) => ({
                    ...state,
                    color: "#CDCDCD",
                    margin: "0",
                    fontSize: "16px",
                    lineHeight: "19px",
                    "@media (max-width: 1279px)": {
                      fontSize: "14px",
                      lineHeight: "17px",
                    },
                  }),
                  singleValue: (state: any) => ({
                    ...state,
                    color: "#0B0C0E",
                    fontSize: "16px",
                    lineHeight: "19px",
                    fontWeight: "500",
                    margin: "0",
                    position: "relative",
                    paddingTop: "0",
                    "&::before": { content: '""' },
                    "@media (max-width: 1279px)": {
                      fontSize: "14px",
                      lineHeight: "17px",
                      paddingTop: "0",
                    },
                    "@media (max-width: 576px)": {
                      lineHeight: "19.6px",
                    },
                  }),
                  control: (state: any) => ({
                    ...state,
                    fontSize: "14px",
                    lineHeight: "26px",
                    color: "#71706F",
                    outline: "none",
                    border: "none",
                    boxShadow: "none",
                    width: "304px",
                    minHeight: "34px",
                    "@media (max-width: 1279px)": {
                      minHeight: "34px",
                      height: "34px",
                    },
                    "@media (max-width: 576px)": {
                      width: "100%",
                    },
                  }),
                  indicatorSeparator: (state) => ({
                    display: "none",
                  }),
                  dropdownIndicator: (base: any, state) => ({
                    ...base,
                    transform: state.selectProps.menuIsOpen
                      ? "rotate(180deg)"
                      : null,
                    color: "#0B0C0E",
                    padding: "0",
                  }),
                  indicatorsContainer: (state: any) => ({
                    ...state,
                    "@media (max-width: 576px)": {
                      height:
                        (discount.discount !== null ||
                          discount.discount_percent !== null) &&
                          pack !== ""
                          ? "39px"
                          : "20px",
                    },
                  }),
                  option: (state: any) => ({
                    ...state,
                    fontSize: "16px",
                    padding: "11px 10px",
                    lineHeight: "22.4px",
                    color: "#0B0C0E",
                    backgroundColor: state.isSelected ? "#F2F4F7" : "#FFF",
                    "&:hover": {
                      background: "#F2F4F7",
                    },
                    "@media (max-width: 1279px)": {
                      fontSize: "14px",
                      lineHeight: "20px",
                      padding: "12px 10px",
                    },
                  }),
                  menu: (state: any) => ({
                    ...state,
                    width: "304px",
                    margin: "10px 0 0",
                    "@media (max-width: 1279px)": {
                      width: "298px",
                    },
                    "@media (max-width: 576px)": {
                      width: "calc(100% - 2px)",
                      margin: "10px 1px 0 1px",
                    },
                  }),
                  menuList: (state: any) => ({
                    ...state,
                    height: "264px",
                    overflow: "scroll",
                    scrollbarColor: "rgba(109, 109, 110, 0.5)",
                    padding: "0",
                    "@media (max-width: 1279px)": {
                      height: "220px",
                    },
                  }),
                }}
              />
            </div>


            <div className="credits-main__method credits-main__section">
              <h4 className="credits-main__title">Payment method:</h4>
              <p className="credits-main__method-description">Stripe Payment</p>
            </div>

            {pack === "" ? (
              <OverlayTrigger
                containerPadding={3}
                overlay={
                  <Tooltip className="credits-tooltip">
                    You need to select a package
                  </Tooltip>
                }
              >
                <button className="btn btn-dark credits-btn unactive">
                  Buy credits
                </button>
              </OverlayTrigger>
            ) : (
              <button
                className={`btn btn-dark credits-btn ${isPurchasing ? "disabled" : ""}`}
                disabled={isPurchasing}
                onClick={async () => {
                  try {
                    setIsPurchasing(true)
                    const selected: any = pack
                    const numericId: number = selected?.id
                    const res: any = await api.events.createStripeCreditsCheckout({
                      id: numericId,
                      successUrl: `${window.location.origin}/payment/success`,
                      cancelUrl: `${window.location.origin}/payment/cancel`,
                    })
                    const url = res?.data?.checkout_url
                    if (url) {
                      window.location.href = url
                    } else {
                      setIsPurchasing(false)
                    }
                  } catch (e) {
                    console.error(e)
                    setIsPurchasing(false)
                  }
                }}
              >
                {isPurchasing ? (
                  <span className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Processing...
                  </span>
                ) : (
                  "Buy credits"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default CreditsPage
